// TABULAÇÕES (desfechos de chamada): o CATÁLOGO da org — o que o vendedor
// escolhe ao encerrar uma ligação — e o relatório que agrega o que foi
// escolhido. Espelha /v1/dispositions (CRUD, restrito a diretor/gerente) e
// GET /v1/calls/dispositions-summary (a agregação).
//
// Não é auditoria como os vizinhos que saíram de api.ts na mesma leva
// (./webhook-logs.js, ./click-to-call-audit.js, ./predictive.js): é um catálogo
// de DOMÍNIO, editado pelo gestor e lido por meia dúzia de telas sem parentesco
// entre si — detalhe da chamada, tabela de ligações, editor de automação,
// relatório. O que os três de auditoria observam, este define.
//
// O RESUMO fica aqui e não num módulo de relatório à parte porque ele é tipado
// PELO catálogo: `byDisposition[].category` é o `DispositionCategory` definido
// logo acima. Dois arquivos aqui seriam um importando o outro por uma união de
// cinco strings.
//
// O "excluir" do catálogo é SOFT-DELETE (DELETE devolve active=false, e
// updateDisposition({ active: true }) reativa): o histórico de chamadas já
// tabuladas continua sabendo dizer o rótulo do desfecho que sumiu da lista.
//
// O mapeamento para o HubSpot (`hubspotDispositionId` + o catálogo de opções do
// portal) mora aqui e não no módulo de integração: o GUID é uma COLUNA da
// tabulação, e quem edita os dois é a mesma tela.
import { api, toApiError } from "./client.js";

export const DISPOSITION_CATEGORIES = ["SUCCESS", "RETRY", "NO_ANSWER", "FAILURE", "DNC"] as const;
export type DispositionCategory = (typeof DISPOSITION_CATEGORIES)[number];

export const DISPOSITION_CATEGORY_LABELS: Record<DispositionCategory, string> = {
  SUCCESS: "Sucesso",
  RETRY: "Rediscar",
  NO_ANSWER: "Não atendeu",
  FAILURE: "Insucesso",
  DNC: "Não perturbe",
};

export interface Disposition {
  id: string;
  organizationId: string;
  label: string;
  category: DispositionCategory;
  sortOrder: number;
  active: boolean;
  hubspotDispositionId: string | null;
}

export async function getDispositions(
  opts: { includeInactive?: boolean } = {},
): Promise<Disposition[]> {
  const q = opts.includeInactive ? "?includeInactive=true" : "";
  const { data, error } = await api<{ dispositions: Disposition[] }>(`/v1/dispositions${q}`);
  if (error) throw toApiError("GET /v1/dispositions", error);
  return data.dispositions;
}

export async function createDisposition(input: {
  label: string;
  category: DispositionCategory;
  sortOrder?: number;
}): Promise<Disposition> {
  const { data, error } = await api<Disposition>("/v1/dispositions", {
    method: "POST",
    body: input,
  });
  if (error) throw toApiError("POST /v1/dispositions", error);
  return data;
}

export async function updateDisposition(
  id: string,
  patch: Partial<{
    label: string;
    category: DispositionCategory;
    sortOrder: number;
    active: boolean;
    hubspotDispositionId: string | null;
  }>,
): Promise<Disposition> {
  const { data, error } = await api<Disposition>(`/v1/dispositions/${id}`, {
    method: "PATCH",
    body: patch,
  });
  if (error) throw toApiError("PATCH /v1/dispositions", error);
  return data;
}

export async function deleteDisposition(id: string): Promise<void> {
  const { error } = await api(`/v1/dispositions/${id}`, { method: "DELETE" });
  if (error) throw toApiError("DELETE /v1/dispositions", error);
}

export interface HubspotDispositionOption {
  id: string;
  label: string;
}

// Resultados de chamada do portal do HubSpot (só gestor). Lista vazia = portal
// sem resposta ou sem resultados: a tela mostra o aviso, não um erro.
export async function getHubspotDispositionOptions(): Promise<HubspotDispositionOption[]> {
  const { data, error } = await api<{ options: HubspotDispositionOption[] }>(
    "/v1/dispositions/hubspot-options",
  );
  if (error) throw toApiError("GET /v1/dispositions/hubspot-options", error);
  return data.options;
}

// GUID -> rótulo do portal. Null quando não mapeado ou quando o resultado foi
// apagado no HubSpot — mostrar o GUID cru não ajudaria ninguém.
export function hubspotOptionLabel(
  options: HubspotDispositionOption[],
  id: string | null,
): string | null {
  if (!id) return null;
  return options.find((o) => o.id === id)?.label ?? null;
}

// --- Resumo das tabulações (relatório) ---
// GET /v1/calls/dispositions-summary — o que foi tabulado no período, por
// desfecho e por agente. Sai de uma rota de CHAMADAS (é a agregação da coluna
// dispositionId da chamada), mas o vocabulário do resultado é o do catálogo
// acima — por isso vive aqui, e não em ./calls.js.

export interface DispositionsSummary {
  total: number;
  byDisposition: {
    dispositionId: string | null;
    label: string | null;
    category: DispositionCategory | null;
    count: number;
  }[];
  byAgentDisposition: {
    userId: string | null;
    agentName: string | null;
    dispositionId: string | null;
    label: string | null;
    count: number;
  }[];
}

export interface DispositionsSummaryQuery {
  from?: string;
  to?: string;
  assignedTo?: string;
  minDuration?: number;
  maxDuration?: number;
  /** Ver CallsQuery.predictive. */
  predictive?: boolean;
}

export async function getDispositionsSummary(
  query: DispositionsSummaryQuery = {},
): Promise<DispositionsSummary> {
  const params = new URLSearchParams();
  if (query.from) params.set("from", query.from);
  if (query.to) params.set("to", query.to);
  if (query.assignedTo) params.set("assignedTo", query.assignedTo);
  if (query.minDuration !== undefined) params.set("minDuration", String(query.minDuration));
  if (query.maxDuration !== undefined) params.set("maxDuration", String(query.maxDuration));
  if (query.predictive) params.set("predictive", "true");
  const qs = params.toString();
  const { data, error } = await api<DispositionsSummary>(
    `/v1/calls/dispositions-summary${qs ? `?${qs}` : ""}`,
  );
  if (error) throw toApiError("GET /v1/calls/dispositions-summary", error);
  return data;
}
